<template>
  <!-- .profile -->
  <div class="container edit-user-template">
    <div class="page-header color-1-100">
      <h1>
        <template v-if="canEditDetails">
          Edit industry details:
        </template>
        {{ this.industry.details.name }}
      </h1>
      <div class="profile-actions float-none">
        <button v-on:click.prevent="deleteIndustry()" class="delete-profile background-1-20 color-2-100"
           v-bind:title="deleteMessage()"
           :class="[{ 'not-allowed': (industry.details.companies && industry.details.companies.length) || industry.details.id === 1 }]">Delete</button>
      </div>
    </div>
    <div class="admin-gm-breadcrumbs" tabindex="-1">
      <router-link class="dashboard active" :to="{name: 'edit-industry', params: {id: industryId}}">Basic data</router-link>
      <router-link class="landing" :to="{ name: 'company-landing', params: { id: landing } }"
                   v-if="landing">Landing</router-link>
      <a href="#" v-on:click.prevent="createLanding()"
              v-if="!landing">Create landing</a>
      <router-link class="landing" :to="{ name: 'company-registration-landing', params: { industry: registrationLanding, company: 'default' } }"
                   v-if="registrationLanding">Registration landing</router-link>
      <a href="#" v-on:click.prevent="createRegistrationLanding()"
              v-if="!registrationLanding">Create registration landing</a>
      <router-link class="companies" :to="{name: 'edit-industry-emails', params: {id: industryId}}">Custom emails</router-link>
      <router-link class="companies" :to="{name: 'edit-industry-styling', params: {id: industryId}}">Custom email and website styling</router-link>
    </div>
    <div class="flex-block">
      <div class="content-left-section">
        <div class="content-section content-section__bordered border-1-20 no-margin-top" ref="industryDetails">
          <article class="personal-info">
            <h3 class="content-section-title color-1-100 background-1-100-before">
              <template v-if="canEditDetails === false">Industry details</template>
              <template v-else>Edit industry details</template>
            </h3>
            <button v-on:click.prevent="openEditDetails()" class="edit-details border-1-40 color-2-100" v-if="canEditDetails === false">Edit</button>
            <div class="form-group form-group-half">
              <div class="form-element">
                <label for="new-industry-name" class="form-label color-2-63">Industry name</label>
                <input type="text" class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                       :class="[{ 'has-error': errors.has('Industry name') } ]"
                       :disabled="canEditDetails === false"
                       v-model="industry.details.name"
                       data-lpignore="true"
                       id="new-industry-name"
                       v-validate="'required'"
                       name="Industry name">
                <span class="form-field-error" v-if="this.errors.has('Industry name')">
                  {{ this.errors.first('Industry name') }}
                </span>
              </div>
            </div>
            <div class="form-group form-group-half">
              <div class="form-element">
                <label for="new-industry-parent" class="form-label color-2-63">Parent industry</label>
                <v-select class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                          :searchable=true
                          :disabled="canEditDetails === false"
                          v-model="industry.details.parent"
                          :options="preparedIndustries"
                          data-vv-name="Parent industry"
                          data-lpignore="true"
                          :class="[{ 'has-error': errors.has('Parent industry') }]"
                          id="new-industry-parent"/>
              </div>
            </div>
            <div class="form-group form-group-half">
              <div class="form-element">
                <label for="new-industry-alias" class="form-label color-2-63">Industry alias</label>
                <input type="text" class="form-control form-control__input background-1-10-disabled  border-1-10-disabled is-disabled"
                       v-model="industry.details.slug"
                       data-lpignore="true"
                       id="new-industry-alias"
                       name="Industry alias"
                       disabled="disabled" >
              </div>
            </div>
          </article>
          <div class="clearfix"></div>
          <div class="update-data update-data__details" v-if="canEditDetails">
            <button v-on:click.prevent="closeEditDetails()" class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes</button>
            <button v-on:click.prevent="updateIndustryDetails()" class="save-edit background-1-100 color-2-100 save-edit-details">Save changes</button>
          </div>
        </div>
        <div class="content-section content-section__bordered border-1-20 profile-section" ref="industryBenchmark">
          <article class="personal-info">
            <h3 class="content-section-title color-1-100 background-1-100-before">
              <template v-if="canEditBenchmark === false">Benchmark</template>
              <template v-else>Edit benchmark</template>
            </h3>
            <a href="#" v-on:click.prevent="openEditBenchmark()" class="edit-details border-1-40 color-2-100"
               v-if="canEditBenchmark === false">Edit</a>
            <div class="form-group">
              <div class="form-element">
                <table class="benchmark-table">
                  <thead>
                  <tr>
                    <th>Policies and procedures</th>
                    <th>Finding talent</th>
                    <th>Assessing talent</th>
                    <th>Retaining talent</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>
                      <div :class="[{ 'is-disabled': canEditBenchmark === false}]">
                        <input title="Policies and procedures benchmark"
                               type="number"
                               class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                               min="0"
                               max="100"
                               :disabled="canEditBenchmark === false"
                               v-model="industry.benchmarkPoliciesAndProcedures"
                               data-lpignore="true">
                      </div>
                    </td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditBenchmark === false}]">
                        <input title="Finding talent benchmark"
                               type="number"
                               class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                               min="0"
                               max="100"
                               :disabled="canEditBenchmark === false"
                               v-model="industry.benchmarkFindingTalent"
                               data-lpignore="true">
                      </div>
                    </td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditBenchmark === false}]">
                        <input title="Accessing talent benchmark"
                               type="number"
                               class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                               min="0"
                               max="100"
                               :disabled="canEditBenchmark === false"
                               v-model="industry.benchmarkAccessingTalent"
                               data-lpignore="true">
                      </div>
                    </td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditBenchmark === false}]">
                        <input title="Retaining talent benchmark"
                               type="number"
                               class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                               min="0"
                               max="100"
                               :disabled="canEditBenchmark === false"
                               v-model="industry.benchmarkRetainingTalent"
                               data-lpignore="true">
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </article>
          <div class="clearfix"></div>
          <div class="update-data update-data__details" v-if="canEditBenchmark">
            <button v-on:click.prevent="closeEditBenchmark()" class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes</button>
            <button v-on:click.prevent="updateIndustryBenchmark()" class="save-edit background-1-100 color-2-100 save-edit-details">Save changes</button>
          </div>
        </div>
        <div class="content-section content-section__bordered border-1-20 profile-section" ref="industryChapters">
          <article class="personal-info">
            <h3 class="content-section-title color-1-100 background-1-100-before">
              <template v-if="canEditChapters === false">Task categories</template>
              <template v-else>Edit task categories</template>
            </h3>
            <button v-on:click.prevent="openEditChapters()" class="edit-details border-1-40 color-2-100"
               v-if="canEditChapters === false">Edit</button>
            <div class="form-group form-group-half">
              <div class="form-element">
                <label for="new-industry-chapter-pp" class="form-label color-2-63">Policies and procedures</label>
                <v-select
                  class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked form-control__select background-1-10  border-1-10 background-2-10-locked border-2-10-locked-multiple"
                  id="new-industry-chapter-pp"
                  :debounce="250"
                  :on-search="getOptionsThingsToConsider"
                  :options="thingsToConsiderOptions"
                  :multiple="false"
                  v-model="industry.chapterPoliciesAndProcedures"
                  :disabled="canEditChapters === false"
                  placeholder="Search..." />
              </div>
              <div class="form-element">
                <label for="new-industry-chapter-ft" class="form-label color-2-63">Finding talent</label>
                <v-select
                  class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked form-control__select background-1-10  border-1-10 background-2-10-locked border-2-10-locked-multiple"
                  id="new-industry-chapter-ft"
                  :debounce="250"
                  :on-search="getOptionsThingsToConsider"
                  :options="thingsToConsiderOptions"
                  :multiple="false"
                  v-model="industry.chapterFindingTalent"
                  :disabled="canEditChapters === false"
                  placeholder="Search..." />
              </div>
            </div>
            <div class="form-group form-group-half">
              <div class="form-element">
                <label for="new-industry-chapter-at" class="form-label color-2-63">Assessing talent</label>
                <v-select
                  class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked form-control__select background-1-10  border-1-10 background-2-10-locked border-2-10-locked-multiple"
                  id="new-industry-chapter-at"
                  :debounce="250"
                  :on-search="getOptionsThingsToConsider"
                  :options="thingsToConsiderOptions"
                  :multiple="false"
                  v-model="industry.chapterAccessingTalent"
                  :disabled="canEditChapters === false"
                  placeholder="Search..." />
              </div>
              <div class="form-element">
                <label for="new-industry-chapter-rt" class="form-label color-2-63">Retaining talent</label>
                <v-select
                  class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked form-control__select background-1-10  border-1-10 background-2-10-locked border-2-10-locked-multiple"
                  id="new-industry-chapter-rt"
                  :debounce="250"
                  :on-search="getOptionsThingsToConsider"
                  :options="thingsToConsiderOptions"
                  :multiple="false"
                  v-model="industry.chapterRetainingTalent"
                  :disabled="canEditChapters === false"
                  placeholder="Search..." />
              </div>
            </div>
          </article>
          <div class="clearfix"></div>
          <div class="update-data update-data__details" v-if="canEditChapters">
            <button v-on:click.prevent="closeEditChapters()" class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes</button>
            <button v-on:click.prevent="updateIndustryChapters()" class="save-edit background-1-100 color-2-100 save-edit-details">Save changes</button>
          </div>
        </div>
        <div class="content-section content-section__bordered border-1-20"
             ref="industrySponsors"
             :class="[{ 'content-section__dark': canEditSponsors === false }]">
          <article class="personal-info">
            <h3 class="content-section-title color-1-100 background-1-100-before">
              <template v-if="canEditSponsors === false">Sponsors</template>
              <template v-else>Edit sponsors</template>
            </h3>
            <button v-on:click.prevent="openEditSponsors()" class="edit-details border-1-40 color-2-100" v-if="canEditSponsors === false">Edit</button>
            <div class="form-group" v-if="canEditSponsors === true">
              <div class="sponsor-list" v-sortable="{ onUpdate: onUpdate }" :options="industry.sponsors">
                <div class="sponsor-item" v-for="(row, index) in industry.sponsors">
                  <div class="sponsor-item-handle"></div>
                  <div class="sponsor-item-logo" :id="'uploadLogo-' + index">
                    <div v-bind:style="{ backgroundImage: getImage(row.logo) }" class="sponsor-item-logo__image" v-if="row.isLogoFileUploaded === true && row.logo !== ''"></div>
                    <input class="hidden" style="display: none" v-model="row.sort = index + 1" />
                    <label :for="'uploadInput-' + index" class="form-label color-2-63 form-label__logo-upload"
                           :class="[{ 'disabled': canEditSponsors === false }]"
                           v-show="row.isLogoFileUploaded === false">
                      +Upload logo
                      <input
                              type="file"
                              :disabled="canEditSponsors === false"
                              :id="'uploadInput-' + index"
                              name="Thumbnail"
                              data-vv-as='"Thumbnail"'
                              v-if="row.isLogoFileUploaded === false"
                              value.sync="row.logo"
                              placeholder="+Upload logo"
                              v-validate.reject="veeValidateFileUploadRules"
                              @change="onThumbnailFileChange($event, index)"
                              class="form-control form-control--file" />
                    </label>
                    <div class="sponsor-item-logo__remove"
                         v-on:click="removeLogo(index)"
                         v-if="row.isLogoFileUploaded === true"></div>
                  </div>
                  <div class="sponsor-item-alt">
                    <input title="Image alt"
                           type="text"
                           class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                           :disabled="canEditSponsors === false"
                           v-model="row.alt"
                           placeholder="Alt"
                           v-on:input="addNewRow(index)"
                           data-lpignore="true">
                  </div>
                  <div class="sponsor-item-url">
                    <input title="Image url"
                           type="text"
                           class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                           :disabled="canEditSponsors === false"
                           v-model="row.url"
                           v-on:input="addNewRow(index)"
                           placeholder="Url"
                           data-lpignore="true">
                  </div>
                  <div class="sponsor-item-remove" v-on:click="removeElement(index)"></div>
                </div>
              </div>
            </div>
            <div class="sponsors-list__edit" v-if="canEditSponsors === false">
              <div class="sponsor-item" v-for="(row, index) in industry.sponsors" v-if="row.logo">
                <div class="sponsor-item-logo" :id="'uploadLogo-' + index">
                  <img :src="getImageFullPath(row.logo)" class="sponsor-item-logo__image" />
                </div>
              </div>
            </div>
            <div class="clearfix"></div>
            <div class="update-data update-data__details" v-if="canEditSponsors">
              <button v-on:click.prevent="closeEditSponsors()" class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes</button>
              <button v-on:click.prevent="updateIndustrySponsors()" class="save-edit background-1-100 color-2-100 save-edit-details">Save changes</button>
            </div>
          </article>
        </div>
      </div>
      <div class="content-right-section anchors">
        <div class="sticky-anchors background-1-40-before">
          <div class="anchor-item color-1-20 details-2" :class="[{ 'active  color-1-100': activeSection === 'industryDetails' }]">
            <button @click.prevent="goToPageSection('industryDetails')">Industry details </button>
          </div>
          <div class="anchor-item color-1-20 benchmark" :class="[{ 'active  color-1-100': activeSection === 'industryBenchmark' }]">
            <button @click.prevent="goToPageSection('industryBenchmark')">Benchmark</button>
          </div>
          <div class="anchor-item color-1-20 benchmark" :class="[{ 'active  color-1-100': activeSection === 'industryChapters' }]">
            <button @click.prevent="goToPageSection('industryChapters')">Task categories</button>
          </div>
          <div class="anchor-item color-1-20 sponsors" :class="[{ 'active  color-1-100': activeSection === 'industrySponsors' }]">
            <button @click.prevent="goToPageSection('industrySponsors')">Sponsors</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- END:.profile -->
</template>

<script>
  import api from '@/api'
  import { mapActions, mapGetters } from 'vuex'
  import AppSelect from '@/components/admin/common/Select'
  import Vue from 'vue'
  import Sortable from 'sortablejs'
  import _ from 'lodash'

  Vue.directive('sortable', {
    inserted: function (el, binding) {
      Sortable.create(el, binding.value || {
        handle: '.sponsor-item-handle'
      })
    }
  })

  export default {
    name: 'edit-industry',
    data () {
      return {
        infMsgTitle: '',
        infMsgDescription: '',
        infMsgClass: '',
        messageDisplay: false,
        canEditDetails: false,
        canEditBenchmark: false,
        canEditChapters: false,
        canEditSponsors: false,
        confOpened: null,
        industry: {
          details: {
            id: '',
            name: '',
            parent: null,
            companies: [],
            slug: ''
          },
          benchmarkPoliciesAndProcedures: '',
          benchmarkFindingTalent: '',
          benchmarkAccessingTalent: '',
          benchmarkRetainingTalent: '',
          chapterPoliciesAndProcedures: '',
          chapterFindingTalent: '',
          chapterAccessingTalent: '',
          chapterRetainingTalent: '',
          sponsors: [
            {
              id: null,
              logo: '',
              alt: '',
              url: '',
              sort: 999999,
              isLogoFileUploaded: false
            }
          ]
        },
        initialData: {
          details: {
            id: '',
            name: '',
            parent: '',
            companies: [],
            slug: ''
          },
          benchmarkPoliciesAndProcedures: '',
          benchmarkFindingTalent: '',
          benchmarkAccessingTalent: '',
          benchmarkRetainingTalent: '',
          chapterPoliciesAndProcedures: '',
          chapterFindingTalent: '',
          chapterAccessingTalent: '',
          chapterRetainingTalent: '',
          sponsors: this.getSponsorList
        },
        confirmation: {
          action: '',
          isShown: false,
          articleId: null,
          i: null,
          style: {
            top: '100px'
          }
        },
        canLeave: false,
        next: null,
        isChanged: false,
        industryId: null,
        activeSection: 'industryDetails',
        thingsToConsiderOptions: [],
        landing: null,
        registrationLanding: null
      }
    },
    computed: {
      ...mapGetters([
        'selectedHightCont',
        'getIndustriesForSelect',
        'getConfData'
      ]),
      preparedIndustries () {
        let industryList = []
        for (let industry in this.getIndustriesForSelect) {
          if (this.getIndustriesForSelect[industry].value !== this.industryId * 1 && this.getIndustriesForSelect[industry].parent !== 1) {
            industryList.push(this.getIndustriesForSelect[industry])
          }
        }
        return industryList
      }
    },
    methods: {
      ...mapActions([
        'clearConfAction',
        'setInformationMsg',
        'getIndustries',
        'setSponsors',
        'initConfAction',
        'authLogout'
      ]),
      getOptionsThingsToConsider (search, loading) {
        loading(true)
        let urlString = `${api.URLS.search}&search=${search}&isArticle=true`
        this.$http.get(urlString, api.headersAuthSettings)
          .then((res) => {
            console.log(res)
            let items = res.body.data.items ? res.body.data.items : []
            this.thingsToConsiderOptions = items.map(item => {
              let industries = item.industries.map(item2 => {
                return item2.name
              })
              industries = industries.length ? ' (' + industries.join(', ') + ')' : ''
              return {
                value: item.id,
                label: item.title + industries
              }
            })
            loading(false)
          })
          .catch((err) => {
            console.log(err)
            loading(false)
          })
      },
      openEditBenchmark: function () {
        this.canEditBenchmark = true
      },
      openEditChapters: function () {
        this.canEditChapters = true
      },
      openEditDetails: function () {
        this.canEditDetails = true
      },
      openEditSponsors: function () {
        this.canEditSponsors = true
      },
      closeEditBenchmark: function () {
        this.industry.benchmarkPoliciesAndProcedures = this.initialData.benchmarkPoliciesAndProcedures
        this.industry.benchmarkFindingTalent = this.initialData.benchmarkFindingTalent
        this.industry.benchmarkAccessingTalent = this.initialData.benchmarkAccessingTalent
        this.industry.benchmarkRetainingTalent = this.initialData.benchmarkRetainingTalent
        this.canEditBenchmark = false
      },
      closeEditChapters: function () {
        this.industry.chapterPoliciesAndProcedures = this.initialData.chapterPoliciesAndProcedures
        this.industry.chapterFindingTalent = this.initialData.chapterFindingTalent
        this.industry.chapterAccessingTalent = this.initialData.chapterAccessingTalent
        this.industry.chapterRetainingTalent = this.initialData.chapterRetainingTalent
        this.canEditChapters = false
      },
      closeEditDetails: function () {
        this.industry.details = this.initialData.details
        this.canEditDetails = false
      },
      closeEditSponsors: function () {
        this.industry.ownerData = this.initialData.ownerData
        this.canEditSponsors = false
      },
      closeMsg: function () {
        this.errors.clear()
      },
      deleteIndustry () {
        if (this.industry.details.companies && this.industry.details.companies.length) return
        this.confOpened = 'delete'
        this.initConfAction({
          actionName: 'removeIndustryById'
        })
      },
      deleteIndustryConfirmed () {
        let id = this.industryId
        this.$http.delete(api.URLS.industry + '/' + id, api.headersAuthSettings)
            .then((res) => {
              this.disableAPI = false
              this.infMsgTitle = this.initialData.details.name + ' industry was successfully deleted.'
              this.infMsgDescription = 'Please note all edits are being logged.'
              this.infMsgClass = 'success'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.goToIndustryList()
            })
            .catch((err) => {
              this.disableAPI = false
              this.infMsgTitle = 'Error. We can\'t delete this industry because '
              for (let error in err.body.error.exception) {
                this.infMsgTitle += err.body.error.exception[error].message + ' '
              }
              if (this.getUserRole === 'gm') {
                this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
              } else {
                this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
              }
              this.infMsgClass = 'error'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              console.log(err)
            })
        this.getConfData.actionConfirmed = false
      },
      fieldChange () {
        this.isChanged = true
      },
      getIndustryData () {
        this.$http.put(api.URLS.industry, {id: this.industryId}, api.headersAuthSettings).then((res) => {
          let industry = this.industry
          // set received data
          industry.details.id = res.body.id
          industry.details.name = res.body.name
          industry.details.slug = res.body.slug
          industry.details.parent = null
          if (res.body.parent !== null) {
            industry.details.parent = {
              id: res.body.parent.id,
              label: res.body.parent.name,
              value: res.body.parent.id
            }
          }
          industry.benchmarkPoliciesAndProcedures = res.body.benchmark_policies_and_procedures
          industry.benchmarkFindingTalent = res.body.benchmark_finding_talent
          industry.benchmarkAccessingTalent = res.body.benchmark_accessing_talent
          industry.benchmarkRetainingTalent = res.body.benchmark_retaining_talent
          industry.details.companies = res.body.companies
          industry.chapterPoliciesAndProcedures = null
          if (res.body.chapter_policies_and_procedures !== null) {
            industry.chapterPoliciesAndProcedures = {
              id: res.body.chapter_policies_and_procedures.id,
              label: res.body.chapter_policies_and_procedures.title,
              value: res.body.chapter_policies_and_procedures.id
            }
          }
          industry.chapterFindingTalent = null
          if (res.body.chapter_finding_talent !== null) {
            industry.chapterFindingTalent = {
              id: res.body.chapter_finding_talent.id,
              label: res.body.chapter_finding_talent.title,
              value: res.body.chapter_finding_talent.id
            }
          }
          industry.chapterAccessingTalent = null
          if (res.body.chapter_accessing_talent !== null) {
            industry.chapterAccessingTalent = {
              id: res.body.chapter_accessing_talent.id,
              label: res.body.chapter_accessing_talent.title,
              value: res.body.chapter_accessing_talent.id
            }
          }
          industry.chapterRetainingTalent = null
          if (res.body.chapter_retaining_talent !== null) {
            industry.chapterRetainingTalent = {
              id: res.body.chapter_retaining_talent.id,
              label: res.body.chapter_retaining_talent.title,
              value: res.body.chapter_retaining_talent.id
            }
          }

          if (res.body.landing) {
            this.landing = res.body.landing.slug
          }

          if (res.body.registration_landing) {
            this.registrationLanding = res.body.registration_landing.slug
          }

          // set initial data
          this.initialData.details.id = industry.details.id
          this.initialData.details.name = industry.details.name
          this.initialData.details.parent = industry.details.parent
          this.initialData.benchmarkPoliciesAndProcedures = industry.benchmarkPoliciesAndProcedures
          this.initialData.benchmarkFindingTalent = industry.benchmarkFindingTalent
          this.initialData.benchmarkAccessingTalent = industry.benchmarkAccessingTalent
          this.initialData.benchmarkRetainingTalent = industry.benchmarkRetainingTalent
          this.initialData.chapterPoliciesAndProcedures = industry.chapterPoliciesAndProcedures
          this.initialData.chapterFindingTalent = industry.chapterFindingTalent
          this.initialData.chapterAccessingTalent = industry.chapterAccessingTalent
          this.initialData.chapterRetainingTalent = industry.chapterRetainingTalent
          this.initialData.details.companies = industry.details.companies
          document.title = industry.details.name + '\'s details | Clear Assured'
          if (window.ga) {
            window.ga('set', 'contentGroup1', 'ADMINISTRATION_AREA')
            window.ga('send', 'pageview', {
              'page': location.pathname,
              'location': location.href,
              'title': document.title
            })
          }
        }).catch((err) => {
          if (err.status === 404) {
            let props = this.$router.resolve({
              name: 'error'
            })
            this.$router.push(props.href)
          }
          if (err.status === 401) {
            this.authLogout()
          }
        })
      },
      getSponsorsData () {
        this.$http.post(api.URLS.sponsors + '/' + this.industryId, {}, api.headersAuthSettings).then((res) => {
          let sponsors = res.body.sponsors
          let newSponsors = []
          if (sponsors.length) {
            this.industry.sponsors = []
            let lastIndex = 0
            for (let sponsor in sponsors) {
              newSponsors.push({
                id: sponsors[sponsor].id,
                logo: sponsors[sponsor].logo,
                alt: sponsors[sponsor].alt,
                url: sponsors[sponsor].url,
                sort: sponsors[sponsor].sort,
                isLogoFileUploaded: true
              })
              lastIndex = lastIndex < sponsors[sponsor].sort ? sponsors[sponsor].sort : lastIndex
            }
            newSponsors.push({
              id: null,
              logo: '',
              alt: '',
              url: '',
              sort: lastIndex + 1,
              isLogoFileUploaded: false
            })
            this.industry.sponsors = _.orderBy(newSponsors, 'sort')
          }
        }).catch((err) => {
          console.log(err)
        })
      },
      updateIndustryDetails () {
        if (this.disableAPI) return
        this.$validator.validateAll()
        if (this.errors.items.length) return
        let industryData = {
          id: this.industry.details.id,
          industryName: this.industry.details.name
        }
        if (this.industry.details.parent !== null) {
          industryData.parent = this.industry.details.parent
        } else {
          industryData.parent = {}
        }
        this.disableAPI = true
        this.$http.put(api.URLS.industry, industryData, api.headersAuthSettings)
            .then((res) => {
              this.disableAPI = false
              this.infMsgTitle = res.body.name + ' industry details was successfully updated.'
              this.infMsgDescription = 'Please note all edits are being logged.'
              this.infMsgClass = 'success'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.getIndustryData()
              this.canEditDetails = !this.canEditDetails
            })
            .catch((err) => {
              this.disableAPI = false
              console.log('err', err)
              this.infMsgTitle = 'Error. We were not able to update this industry because '
              for (let error in err.body.error.exception) {
                this.infMsgTitle += err.body.error.exception[error].message + ' '
              }
              this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
              this.infMsgClass = 'error'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.getIndustryData()
            })
      },
      updateIndustryBenchmark () {
        if (this.disableAPI) return
        let benchmark = {
          id: this.industry.details.id,
          benchmarkFindingTalent: this.industry.benchmarkFindingTalent,
          benchmarkPoliciesAndProcedures: this.industry.benchmarkPoliciesAndProcedures,
          benchmarkAccessingTalent: this.industry.benchmarkAccessingTalent,
          benchmarkRetainingTalent: this.industry.benchmarkRetainingTalent
        }
        this.disableAPI = true
        this.$http.put(api.URLS.industry, benchmark, api.headersAuthSettings)
            .then((res) => {
              this.disableAPI = false
              this.infMsgTitle = res.body.name + ' industry benchmark was successfully updated.'
              this.infMsgDescription = 'Please note all edits are being logged.'
              this.infMsgClass = 'success'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.canEditBenchmark = false
              this.getIndustryData()
            })
            .catch((err) => {
              this.getIndustryData()
              this.disableAPI = false
              this.infMsgTitle = 'Error. We were not able to update this industry because '
              for (let error in err.body.error.exception) {
                this.infMsgTitle += err.body.error.exception[error].message + ' '
              }
              this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
              this.infMsgClass = 'error'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.canEditBenchmark = false
            })
        this.messageDisplay = true
      },
      updateIndustryChapters () {
        if (this.disableAPI) return
        let chapter = {
          id: this.industry.details.id,
          chapterFindingTalent: this.industry.chapterFindingTalent || {},
          chapterPoliciesAndProcedures: this.industry.chapterPoliciesAndProcedures || {},
          chapterAccessingTalent: this.industry.chapterAccessingTalent || {},
          chapterRetainingTalent: this.industry.chapterRetainingTalent || {}
        }
        this.disableAPI = true
        this.$http.put(api.URLS.industry, chapter, api.headersAuthSettings)
            .then((res) => {
              this.disableAPI = false
              this.infMsgTitle = res.body.name + ' industry task categories was successfully updated.'
              this.infMsgDescription = 'Please note all edits are being logged.'
              this.infMsgClass = 'success'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.canEditChapters = false
              this.getIndustryData()
            })
            .catch((err) => {
              this.getIndustryData()
              this.disableAPI = false
              this.infMsgTitle = 'Error. We were not able to update this industry because '
              for (let error in err.body.error.exception) {
                this.infMsgTitle += err.body.error.exception[error].message + ' '
              }
              this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
              this.infMsgClass = 'error'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.canEditChapters = false
            })
        this.messageDisplay = true
      },
      updateIndustrySponsors () {
        if (this.disableAPI) return
        this.$validator.validateAll()
        if (this.errors.items.length) return
        let sponsors = []
        for (let sponsor in this.industry.sponsors) {
          if (this.industry.sponsors[sponsor].logo !== '') {
            sponsors.push({
              logo: this.industry.sponsors[sponsor].logo,
              alt: this.industry.sponsors[sponsor].alt,
              url: this.industry.sponsors[sponsor].url,
              order: this.industry.sponsors[sponsor].sort
            })
          }
        }
        let sponsorsData = {
          id: this.industry.details.id,
          sponsors: sponsors
        }
        this.disableAPI = true
        this.$http.put(api.URLS.industry, sponsorsData, api.headersAuthSettings)
            .then((res) => {
              this.disableAPI = false
              this.infMsgTitle = res.body.name + ' industry sponsors list was successfully updated.'
              this.infMsgDescription = 'Please note all edits are being logged.'
              this.infMsgClass = 'success'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
              this.canEditSponsors = !this.canEditSponsors
            })
            .catch((err) => {
              this.disableAPI = false
              this.infMsgTitle = 'Error. We were not able to update this industry because '
              for (let error in err.body.error.exception) {
                this.infMsgTitle += err.body.error.exception[error].message + ' '
              }
              this.infMsgDescription = 'Please note all edits are being logged.'
              this.infMsgClass = 'error'
              this.setInformationMsg({
                isShown: true,
                title: this.infMsgTitle,
                text: this.infMsgDescription,
                className: this.infMsgClass
              })
            })
      },
      createLanding: function () {
        if (this.disableAPI) return

        let landingData = {
          title: this.industry.details.name,
          slug: this.industry.details.slug,
          body: JSON.stringify([]),
          description: 'Landing page for industry ' + this.industry.details.name,
          landing: true,
          industry: this.industry.details.id
        }

        this.disableAPI = true
        this.$http.post(api.URLS.staticpage, landingData, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            if (res.body.slug) {
              let props = this.$router.resolve({
                name: 'edit-company-landing',
                params: {id: res.body.slug}
              })
              this.$router.push(props.href)
            }
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We were not able to create landing page for industry: "' + this.industry.details.name + '" because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = ''
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
          })
      },
      createRegistrationLanding: function () {
        if (this.disableAPI) return

        let registrationLandingData = {
          title: this.industry.details.name,
          slug: this.industry.details.slug,
          body: JSON.stringify([]),
          description: 'Registration landing page for industry ' + this.industry.details.name,
          registrationLanding: true,
          industry: this.industry.details.id
        }

        this.disableAPI = true
        this.$http.post(api.URLS.staticpage, registrationLandingData, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            if (res.body.slug) {
              let props = this.$router.resolve({
                name: 'edit-company-registration-landing',
                params: {industry: res.body.slug}
              })
              this.$router.push(props.href)
            }
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We were not able to create registration landing page for industry: "' + this.industry.details.name + '" because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = ''
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
          })
      },
      goToIndustryList () {
        let props = this.$router.resolve({
          name: 'industries'
        })
        this.$router.push(props.href)
      },
      addNewRow: function (index) {
        if (this.industry.sponsors.length - 1 === index) {
          this.industry.sponsors.push({
            id: null,
            logo: '',
            alt: '',
            url: '',
            sort: index + 1,
            isLogoFileUploaded: false
          })
        }
      },
      removeElement: function (index) {
        if (this.industry.sponsors.length === 1) {
          this.industry.sponsors = [
            {
              id: null,
              logo: '',
              alt: '',
              url: '',
              sort: 0,
              isLogoFileUploaded: false
            }
          ]
        } else {
          this.industry.sponsors.splice(index, 1)
        }
      },
      veeValidateFileUploadRules () {
        return {
          required: true,
          mimes: 'image/*'
        }
      },
      onThumbnailFileChange (e, index) {
        let self = this
        let input = document.getElementById(e.target.id)
        let thisItem = input.closest('.sponsor-item')
        if (input.files && input.files[0] && input.files[0].name.match(/.(jpg|jpeg|png|gif|svg)$/i)) {
          let reader = new FileReader()
          reader.onloadstart = function () {
            thisItem.classList.add('loading')
          }
          reader.onload = function (e) {
            let fileName = input.files[0].name
            let extension = fileName.split('.').pop().toLowerCase()
            if (self.disableAPI) return
            let pageData = {
              file: e.target.result,
              extension: extension
            }
            self.disableAPI = true
            self.$http.post(api.URLS.uploadFile, pageData, api.headersAuthSettings).then((res) => {
              self.disableAPI = false
              if (Array.isArray(res.body)) {
                alert(res.body[0] + '. Please try again.')
              }
              self.industry.sponsors[index].logo = res.body
              self.industry.sponsors[index].isLogoFileUploaded = true
              if (self.industry.sponsors.length - 1 === index) {
                self.industry.sponsors.push({
                  id: null,
                  logo: '',
                  alt: '',
                  url: '',
                  sort: index + 1,
                  isLogoFileUploaded: false
                })
              }
            }).catch((err) => {
              self.disableAPI = false
              alert('Error on file upload. Please try again')
              console.log('err', err)
            })
          }
          reader.readAsDataURL(input.files[0])
          reader.onloadend = function () {
            if (this.readyState === 2) {
              thisItem.classList.remove('loading')
            }
          }
        }
      },
      getImagePath () {
        return api.serverURL + '/uploads/images/content/'
      },
      isBase64 (payload) {
        if (!payload) {
          return false
        }
        return payload.indexOf('data:') !== -1
      },
      getImage (payload) {
        if (!payload) {
          return ''
        }
        return this.isBase64(payload) ? 'url(' + payload + ')' : 'url(' + this.getImagePath() + payload + ')'
      },
      getImageFullPath (payload) {
        if (!payload) {
          return ''
        }
        return this.isBase64(payload) ? payload : this.getImagePath() + payload
      },
      removeLogo (index) {
        this.industry.sponsors[index].isLogoFileUploaded = false
        this.industry.sponsors[index].logo = ''
      },
      deleteMessage () {
        if (this.industry.details.id === 1) {
          return 'Default industry can\'t be deleted.'
        } else if (this.industry.details.companies && this.industry.details.companies.length) {
          return 'An industry can\'t be deleted if it has companies associated with it.'
        } else {
          return 'Delete industry.'
        }
      },
      onUpdate: function (event) {
        this.industry.sponsors.splice(event.newIndex, 0, this.industry.sponsors.splice(event.oldIndex, 1)[0])
      },
      goToPageSection (ref) {
        let element = this.$refs[ref]
        if (element) {
          let here = element[0] ? element[0] : element
          here.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        }
      },
      posY (elm) {
        let test = elm[0] ? elm[0] : elm
        let top = 0
        if (test && test.tagName) {
          while (test !== null && test.tagName.toLowerCase() !== 'body') {
            top += test.offsetTop
            test = test.offsetParent
          }
        }
        return top
      },
      scrollY () {
        if (window.pageYOffset) {
          return window.pageYOffset
        }
        return Math.max(document.documentElement.scrollTop, document.body.scrollTop)
      },
      isScrolledIntoView (el) {
        let elem = el[0] ? el[0] : el
        let docViewTop = this.scrollY()
        let docViewBottom = docViewTop + window.outerHeight
        let elemTop = this.posY(elem) + 300
        let elemBottom = elemTop + elem.offsetHeight
        return ((elemBottom <= docViewBottom) && (elemTop >= docViewTop))
      },
      handleScroll () {
        let sections = this.$refs
        for (let index in sections) {
          if (!index.match(/article/)) {
            let section = sections[index]
            if (section && this.isScrolledIntoView(section)) {
              this.activeSection = index
            }
          }
        }
      }
    },
    components: {
      AppSelect
    },
    watch: {
      'getConfData.actionConfirmed' (value) {
        if (value) {
          if (this.confOpened === 'delete') this.deleteIndustryConfirmed()
          this.clearConfAction()
        }
      }
    },
    beforeMount () {
      this.getIndustries()
    },
    created () {
      this.industryId = this.$route.params.id
      window.addEventListener('scroll', this.handleScroll)
    },
    mounted () {
      this.getIndustryData()
      this.getSponsorsData()
    },
    destroyed () {
      window.removeEventListener('scroll', this.handleScroll)
    }
  }
</script>

<style src='@/assets/scss/components/profile.scss' lang='scss' />



// WEBPACK FOOTER //
// EditIndustry.vue?dfdb59dc